import React from 'react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Checkbox } from '../common/Checkbox';
import { Select } from '../common/Select';
import { useAuth } from '../../contexts/AuthContext';
import { submitAffiliateApplication } from '../../services/firebase/affiliates';

type SocialMediaHandles = {
  instagram: string;
  tiktok: string;
  facebook: string;
  linkedin: string;
  x: string;
  youtube: string;
  other: string;
};

type AffiliateFormData = {
  fullName: string;
  email: string;
  country: string;
  socialMediaHandles: SocialMediaHandles;
  website: string;
  blog: string;
  promotionIntention: string;
  agreeToTerms: boolean;
};
type FieldErrors = Partial<Record<keyof AffiliateFormData, string>>;

const initialFormValues: AffiliateFormData = {
  fullName: '',
  email: '',
  country: '',
  socialMediaHandles: {
    instagram: '',
    tiktok: '',
    facebook: '',
    linkedin: '',
    x: '',
    youtube: '',
    other: '',
  },
  website: '',
  blog: '',
  promotionIntention: '',
  agreeToTerms: false,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidUrl = (value: string) => {
  if (!value) {
    return true;
  }

  try {
    new URL(value);
    return true;
  } catch (_error) {
    try {
      new URL(`https://${value}`);
      return true;
    } catch (__error) {
      return false;
    }
  }
};

const validateForm = (values: AffiliateFormData): FieldErrors => {
  const validationErrors: FieldErrors = {};

  if (!values.fullName.trim()) {
    validationErrors.fullName = 'Full Name is required';
  }

  if (!values.email.trim()) {
    validationErrors.email = 'Email is required';
  } else if (!emailPattern.test(values.email)) {
    validationErrors.email = 'Invalid email format';
  }

  if (!values.country.trim()) {
    validationErrors.country = 'Country of Residence is required';
  }

  const hasSocialHandle = Object.values(values.socialMediaHandles).some((handle) => handle.trim());

  if (!hasSocialHandle) {
    validationErrors.socialMediaHandles = 'At least one social media link is required';
  }

  if (values.website && !isValidUrl(values.website)) {
    validationErrors.website = 'Must be a valid URL';
  }

  if (values.blog && !isValidUrl(values.blog)) {
    validationErrors.blog = 'Must be a valid URL';
  }

  if (!values.promotionIntention.trim()) {
    validationErrors.promotionIntention = 'Promotion intention is required';
  }

  if (!values.agreeToTerms) {
    validationErrors.agreeToTerms = "You must agree to the T's and C's";
  }

  return validationErrors;
};

const countries = [
  '',
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo',
  'Costa Rica',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Holy See',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Republic of Ireland',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];

const countryOptions = countries.map((country) => ({
  value: country,
  label: country || 'Select Country',
}));

export const AffiliateRegistrationForm: React.FC = () => {
  const { user } = useAuth();
  const [formValues, setFormValues] = React.useState<AffiliateFormData>(initialFormValues);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      setFormValues((prev) => ({
        ...prev,
        fullName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleInputChange = <T extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    field: Exclude<keyof AffiliateFormData, 'socialMediaHandles'>,
  ) => (event: React.ChangeEvent<T>) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSocialMediaChange = (platform: keyof SocialMediaHandles) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;

    setFormValues((prev) => ({
      ...prev,
      socialMediaHandles: {
        ...prev.socialMediaHandles,
        [platform]: value,
      },
    }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const validationErrors = validateForm(formValues);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    setErrors({});

    try {
      // Ensure userId is not undefined (Firestore doesn't like undefined)
      const applicationData = {
        ...formValues,
        userId: user?.uid || null,
      };
      
      await submitAffiliateApplication(applicationData);
      setIsSuccess(true);
      setFormValues(initialFormValues);
    } catch (error) {
      console.error('Failed to submit application', error);
      // Ideally show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-extrabold text-green-600 mb-6">
          Application Received!
        </h2>
        <p className="text-gray-600 mb-8 text-lg">
          Thank you for applying to the trAInedup.ai affiliate program. We have received your application and will review it shortly. You will receive an email with further instructions once your application has been processed.
        </p>
        <Button onClick={() => setIsSuccess(false)} variant="primary">
          Submit Another Application
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-3xl mx-auto">
      <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-6">
        Affiliate Registration
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Join the trAInedup.ai affiliate program today!
      </p>

      <form onSubmit={onSubmit} className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-4">
          Personal Information
        </h3>
        <Input
          label="Full Name (Required)"
          type="text"
          value={formValues.fullName}
          onChange={handleInputChange('fullName')}
          error={errors.fullName}
          fullWidth
        />
        <Input
          label="Email Address (Required)"
          type="email"
          value={formValues.email}
          onChange={handleInputChange('email')}
          error={errors.email}
          fullWidth
        />
        <Select
          label="Country of Residence (Required)"
          options={countryOptions}
          value={formValues.country}
          onChange={handleInputChange('country')}
          error={errors.country}
        />

        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800">Social Media Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Instagram"
              type="text"
              value={formValues.socialMediaHandles.instagram}
              onChange={handleSocialMediaChange('instagram')}
            />
            <Input
              label="TikTok"
              type="text"
              value={formValues.socialMediaHandles.tiktok}
              onChange={handleSocialMediaChange('tiktok')}
            />
            <Input
              label="Facebook"
              type="text"
              value={formValues.socialMediaHandles.facebook}
              onChange={handleSocialMediaChange('facebook')}
            />
            <Input
              label="LinkedIn"
              type="text"
              value={formValues.socialMediaHandles.linkedin}
              onChange={handleSocialMediaChange('linkedin')}
            />
            <Input
              label="X"
              type="text"
              value={formValues.socialMediaHandles.x}
              onChange={handleSocialMediaChange('x')}
            />
            <Input
              label="YouTube"
              type="text"
              value={formValues.socialMediaHandles.youtube}
              onChange={handleSocialMediaChange('youtube')}
            />
            <div className="md:col-span-2">
              <Input
                label="Other"
                type="text"
                value={formValues.socialMediaHandles.other}
                onChange={handleSocialMediaChange('other')}
              />
            </div>
          </div>
          {errors.socialMediaHandles && (
            <p className="text-sm text-red-600">{errors.socialMediaHandles}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Website"
            type="url"
            value={formValues.website}
            onChange={handleInputChange('website')}
            error={errors.website}
          />
          <Input
            label="Blog"
            type="url"
            value={formValues.blog}
            onChange={handleInputChange('blog')}
            error={errors.blog}
          />
        </div>

        <h3 className="text-xl font-semibold text-gray-800 border-b pb-2 pt-4 mb-4">
          Promotional Intentions
        </h3>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            How will you promote trAInedup.ai?
          </label>
          <textarea
            className={`block w-full rounded-lg border ${errors.promotionIntention ? 'border-red-500 focus:ring-red-500' : 'border-neutral-300 focus:ring-blue-500'} bg-white px-4 py-3 text-base text-neutral-800 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 min-h-[120px]`}
            value={formValues.promotionIntention}
            onChange={handleInputChange('promotionIntention')}
          />
          {errors.promotionIntention && (
            <p className="text-sm text-red-600">{errors.promotionIntention}</p>
          )}
        </div>

        <h3 className="text-xl font-semibold text-gray-800 border-b pb-2 pt-4 mb-4">
          T's and C's
        </h3>
        <div className="border p-4 rounded-lg bg-gray-50 max-h-60 overflow-y-auto text-sm text-gray-700">
          {/* Terms and Conditions Content from PDF */}
          <h4 className="font-bold mb-2">1. Program Eligibility</h4>
          <p className="mb-4">Affiliates must be at least eighteen years old and must provide accurate registration information. Applications may be approved or declined based on content quality, audience relevance, or compliance history. trAInedup.ai may remove or decline accounts that appear inactive, misleading, fraudulent, or harmful to the brand.</p>

          <h4 className="font-bold mb-2">2. Commission Structure</h4>
          <p className="mb-4">Affiliates earn thirty percent commission on every paid membership created through their unique link. Commission is recurring for the full lifetime of each active subscription. There is no time limit or cap on the length of earnings. Commissions are calculated on the net amount paid by the customer.</p>

          <h4 className="font-bold mb-2">3. Pay out Terms</h4>
          <p className="mb-4">Commissions are paid within 14 days of registration. Affiliates must provide valid payment details before receiving pay outs. Pay outs are only made for cleared and successful payments from referred members. Failed or refunded payments do not qualify for commission. trAInedup.ai may withhold payouts temporarily if fraudulent activity is suspected.</p>

          <h4 className="font-bold mb-2">4. Tracking and Attribution</h4>
          <p className="mb-4">Each affiliate receives a unique referral link. Sign ups and commissions are tracked through this link.</p>

          <h4 className="font-bold mb-2">5. Promotion Rules</h4>
          <p className="mb-4">Affiliates may promote trAInedup.ai on their social channels, websites, newsletters, videos, and other approved platforms once approved. The following activities are not allowed:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Spam or mass unsolicited messaging</li>
            <li>Misleading claims about earnings or results</li>
            <li>False guarantees or unapproved statements</li>
            <li>Pretending to represent trAInedup.ai directly</li>
            <li>Posting offensive or harmful content linked to the program</li>
            <li>Affiliates must follow platform rules and must clearly disclose affiliate partnerships in their content.</li>
          </ul>

          <h4 className="font-bold mb-2">6. Branding and Content Use</h4>
          <p className="mb-4">Affiliates may use logos and materials provided, they may not be altered in a way that changes the brand identity.</p>

          <h4 className="font-bold mb-2">7. Refunds and Reversals</h4>
          <p className="mb-4">If a member cancels within the seven-day refund period, any commission linked to that transaction is reversed. If a chargeback or failed payment occurs, no commission is awarded for that payment cycle.</p>

          <h4 className="font-bold mb-2">8. Prohibited Activities</h4>
          <p className="mb-4">The following actions result in immediate removal from the program:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Fraudulent sign ups or self referrals</li>
            <li>Creating fake accounts or registering on behalf of customers</li>
            <li>Promoting hateful, sexual, or illegal content</li>
            <li>Sharing internal affiliate materials publicly</li>
          </ul>

          <h4 className="font-bold mb-2">9. Termination</h4>
          <p className="mb-4">trAInedup.ai reserves the right to suspend or terminate an affiliate account at any time if policies are violated. Affiliates may also close their account at any time by request. Payments will continue to be paid while the affiliate has a live account even if their account is inactive at any point.</p>

          <h4 className="font-bold mb-2">10. Policy Updates</h4>
          <p className="mb-4">trAInedup.ai may update these policies as needed. Affiliates will be notified of important changes. Continued participation after an update means the affiliate accepts the new terms.</p>
        </div>

        <Checkbox
          label="Do you agree to the Affiliate T's and C's?"
          checked={formValues.agreeToTerms}
          onChange={handleInputChange<HTMLInputElement>('agreeToTerms')}
          error={errors.agreeToTerms}
        />
        <p className="text-sm text-gray-700">
          One of the trAInedup.ai team will review your application and reply within 48 hours with a decision. If you are approved, your decision email will include your dashboard login details.
        </p>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition duration-300 ease-in-out"
        >
          {isSubmitting ? 'Registering...' : 'Register as an Affiliate'}
        </Button>
      </form>
    </div>
  );
};
